import React from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';

/**
 * Lloyd's 8-stage case progression (Client view _ Raza.docx):
 *   (1) pending payment → (2) LOR sent → (3) LOR confirmed → (4) Pre-trial scheduled →
 *   (5) Offer received / sent → (6) Offer accepted → (7) Deferral / DSC conditions → (8) closed
 *
 * Short labels render in the bar; full labels show on hover.
 */
export const CASE_STAGES: { short: string; full: string }[] = [
  { short: 'Pending', full: '1. Pending payment' },
  { short: 'LOR sent', full: '2. LOR sent' },
  { short: 'LOR confirmed', full: '3. LOR confirmed' },
  { short: 'Pre-trial', full: '4. Pre-trial scheduled' },
  { short: 'Offer', full: '5. Offer received / sent to client' },
  { short: 'Accepted', full: '6. Offer accepted (pending docs) / rejected / trial / appeal' },
  { short: 'Conditions', full: '7. Deferral / DSC conditions (affidavit / driving record)' },
  { short: 'Closed', full: '8. Closed' },
];

// Map our internal TicketStatus names onto Lloyd's 8 stages (0-indexed). Best-effort;
// stages not directly represented by a status (e.g. Conditions) are still shown in the bar.
const STATUS_TO_STAGE: Record<string, number> = {
  'citation received (Approving)': 0,
  'case accepted (Approved)': 0,
  'letter of representation sent (Active)': 1,
  'court respond by date satisfied (Pending)': 2,
  'New court date': 3,
  'negotiating with prosecutor': 4,
  'offer received': 4,
  'waiting for client to reject or accept offer': 4,
  'waiting for judge to approve': 5,
  'deferral/DSC conditions': 6,
  'case closed/dismissed (closed)': 7,
};

// Reverse map: the canonical internal status to write when a manager clicks a stage to move a
// case there. Stage 6 (Deferral / DSC conditions) is set automatically from an offer when it
// contains the trigger phrases (G.6), and staff can also set/override it here.
const STAGE_TO_STATUS: Record<number, string> = {
  0: 'case accepted (Approved)',
  1: 'letter of representation sent (Active)',
  2: 'court respond by date satisfied (Pending)',
  3: 'New court date',
  4: 'offer received',
  5: 'waiting for judge to approve',
  6: 'deferral/DSC conditions',
  7: 'case closed/dismissed (closed)',
};

export const CLOSED_STAGE_INDEX = 7;

export function stageForStatus(statusName: string | null | undefined): number {
  if (!statusName) return -1;
  return statusName in STATUS_TO_STAGE ? STATUS_TO_STAGE[statusName] : -1;
}

export function statusForStage(stageIndex: number): string | null {
  return STAGE_TO_STATUS[stageIndex] ?? null;
}

interface CaseStatusTimelineProps {
  statusName: string | null | undefined;
  /**
   * Override for the highlighted stage. Callers pass the LOR-flag-aware index
   * (see deriveStageIndex) so imported / no-status cases still light up a stage
   * instead of rendering an all-grey bar. Falls back to statusName mapping when omitted.
   */
  currentStageIndex?: number;
  /**
   * When provided, settable stages become clickable and call this with the canonical
   * internal status name + the stage's full label. Omit for a read-only bar.
   */
  onSelectStage?: (statusName: string, stageLabel: string) => void;
  /** Disables interaction (e.g. while a status change is in flight). */
  disabled?: boolean;
  /**
   * Traffic or non-traffic (Lloyd, Aug 6). Null on cases that pre-date the field, which are traffic.
   *
   * The eight stages describe a traffic case: LOR sent, LOR confirmed, pre-trial, deferral. A county
   * or district criminal matter passes through none of them, so showing the bar would invite staff
   * to move a case to a stage that does not exist for it.
   */
  matterType?: string | null;
}

const CaseStatusTimeline: React.FC<CaseStatusTimelineProps> = ({
  statusName,
  currentStageIndex,
  onSelectStage,
  disabled = false,
  matterType,
}) => {
  const current = currentStageIndex ?? stageForStatus(statusName);

  if (matterType === 'NON_TRAFFIC') {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
        Non-traffic matter — the traffic progression does not apply. Tracked in Clio.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', py: 0.5 }}>
      {CASE_STAGES.map((stage, i) => {
        const done = current >= 0 && i < current;
        const isCurrent = i === current;
        const bg = isCurrent ? 'primary.main' : done ? 'primary.light' : 'grey.200';
        const color = isCurrent || done ? 'primary.contrastText' : 'text.secondary';
        const targetStatus = statusForStage(i);
        const settable = Boolean(onSelectStage) && targetStatus !== null && !isCurrent;
        const cellSx = {
          flex: '1 1 0',
          minWidth: 72,
          textAlign: 'center' as const,
          bgcolor: bg,
          color,
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          fontWeight: isCurrent ? 700 : 400,
          border: isCurrent ? '2px solid' : '1px solid',
          borderColor: isCurrent ? 'primary.dark' : 'divider',
          width: '100%',
        };
        const label = (
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
            {i + 1}. {stage.short}
          </Typography>
        );
        const tooltipTitle = settable
          ? `${stage.full} — click to move here`
          : !onSelectStage
          ? stage.full
          : targetStatus === null
          ? `${stage.full} (set from the case's status controls)`
          : stage.full;

        return (
          <Tooltip key={stage.short} title={tooltipTitle} arrow>
            <Box sx={{ flex: '1 1 0', minWidth: 72, display: 'flex' }}>
              {settable ? (
                <ButtonBase
                  disabled={disabled}
                  onClick={() => onSelectStage!(targetStatus!, stage.full)}
                  sx={{
                    ...cellSx,
                    cursor: disabled ? 'default' : 'pointer',
                    transition: 'filter 120ms',
                    '&:hover': disabled ? {} : { filter: 'brightness(0.92)' },
                  }}
                >
                  {label}
                </ButtonBase>
              ) : (
                <Box sx={cellSx}>{label}</Box>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default CaseStatusTimeline;
